import { shadeTierColor } from "../../../../utils/loyalty/tiers";
import { BADGE_GLOSS_TRANSFORM, BADGE_PATHS } from "../artPaths";
import styles from "./LoyaltyTierBadge.module.scss";

/** Rendered sizes taken from Figma: 18x20 inline with text, 24x27 standalone. */
const BADGE_SIZES = {
  small: { width: 18, height: 20 },
  medium: { width: 24, height: 27 },
  large: { width: 28, height: 32 },
} as const;

const gradientKey = (color: string) => color.replace(/[^\da-z]/gi, "");

/**
 * The hexagon artwork on its own 24x27 grid, so both the standalone badge and
 * the ribboned medal draw from one copy of the Figma paths.
 */
export const LoyaltyTierHexagon = ({
  rank,
  color,
  showRank = true,
}: {
  rank: number;
  color: string;
  /** The plain hexagon, used where Figma omits the engraved number. */
  showRank?: boolean;
}) => (
  <>
    <path d={BADGE_PATHS.outer} fill={color} />
    <path d={BADGE_PATHS.frame} fill="#000000" fillOpacity="0.3" />
    <path d={BADGE_PATHS.middle} fill={color} />
    <path d={BADGE_PATHS.inner} fill="#000000" fillOpacity="0.4" />
    <path
      d={BADGE_PATHS.face}
      fill={`url(#tier_badge_${gradientKey(color)})`}
    />
    {/* Overlaid last in Figma, but kept under the rank so the engraving stays
        as legible as the design's own. */}
    <path
      d={BADGE_PATHS.gloss}
      transform={BADGE_GLOSS_TRANSFORM}
      fill={`url(#tier_badge_gloss_${gradientKey(color)})`}
      fillOpacity="0.3"
      style={{ mixBlendMode: "overlay" }}
    />
    {showRank && (
      <text
        className={styles.tier_badge_rank}
        x="12"
        y="17.5"
        textAnchor="middle"
        // Engraved: the tier colour under Figma's 30% black.
        fill={shadeTierColor(color)}
      >
        {rank}
      </text>
    )}
  </>
);

export const LoyaltyTierHexagonGradient = ({ color }: { color: string }) => (
  <>
    <linearGradient
      id={`tier_badge_${gradientKey(color)}`}
      x1="11.83"
      y1="2.1"
      x2="12.15"
      y2="22.34"
      gradientUnits="userSpaceOnUse"
    >
      <stop stopColor="#FFFFFF" />
      <stop offset="1" stopColor={color} />
    </linearGradient>
    {/* The gloss runs across the hexagon, not down it, and Figma keeps both of
        its stops fixed for every tier. */}
    <linearGradient id={`tier_badge_gloss_${gradientKey(color)}`}>
      <stop stopColor="#FFFFFF" />
      <stop offset="1" stopColor="#FFDACC" />
    </linearGradient>
  </>
);

interface LoyaltyTierBadgeProps {
  /** 1-based tier position engraved on the hexagon. */
  rank: number;
  /** Server-configured tier colour. */
  color: string;
  size?: keyof typeof BADGE_SIZES;
  title?: string;
  /**
   * Figma engraves the tier number on the standalone badge and the medal, but
   * leaves it off the small hexagon next to the "Rewards" tab and the
   * "All Rewards" heading (15404:108479).
   */
  showRank?: boolean;
}

/**
 * Figma hexagon tier badge. The artwork is tinted with the tier colour coming
 * from loyalty-service and engraved with the tier position, so a restaurant
 * with any number of custom-coloured tiers renders its own badge.
 */
export const LoyaltyTierBadge = ({
  rank,
  color,
  size = "medium",
  title,
  showRank = true,
}: LoyaltyTierBadgeProps) => {
  const { width, height } = BADGE_SIZES[size];

  return (
    <svg
      className={styles.tier_badge}
      width={width}
      height={height}
      viewBox="0 0 24 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title && <title>{title}</title>}
      <defs>
        <LoyaltyTierHexagonGradient color={color} />
      </defs>
      <LoyaltyTierHexagon rank={rank} color={color} showRank={showRank} />
    </svg>
  );
};
